import { snakeCase } from 'lodash/fp';

import { Verification } from 'dashboard/models/verification';
import { VerificationTemplate } from 'dashboard/models/verification-template';
import { VerificationTemplateVersion } from 'dashboard/models/verification-template-version';
import { ApiModel } from 'lib/interfaces/api';
import { CheckType } from 'lib/verificationCheck';

export const VERIFICATION_TYPE_NAMES: Record<Verification['type'], string> = {
  [ApiModel.VerificationAamva]: 'AAMVA verification',
  [ApiModel.VerificationBusinessWebsite]: 'Business Website verification',
  [ApiModel.VerificationCertificateKorea]: 'Korean Digital Certificate verification',
  [ApiModel.VerificationDatabase]: 'Database verification',
  [ApiModel.VerificationDatabaseAadhaar]: 'Database Aadhaar verification',
  [ApiModel.VerificationDatabaseBusiness]: 'Business Registry verification',
  [ApiModel.VerificationDatabaseBusinessFootprint]: 'Business Footprint verification',
  [ApiModel.VerificationDatabaseEcbsv]: 'Database eCBSV verification',
  [ApiModel.VerificationDatabasePhoneCarrier]: 'Database Phone Carrier verification',
  [ApiModel.VerificationDatabaseSerpro]: 'Database Serpro verification',
  [ApiModel.VerificationDatabaseStandard]: 'Database Standard verification',
  [ApiModel.VerificationDatabaseTin]: 'Database TIN verification',
  [ApiModel.VerificationDatabaseVatNumber]: 'Database VAT Number verification',
  [ApiModel.VerificationDigitalIdFinnishTrustNetwork]:
    'Digital ID Finnish Trust Network verification',
  [ApiModel.VerificationDigitalIdOneId]: 'Digital ID OneID verification',
  [ApiModel.VerificationDigitalIdSerpro]: 'Digital ID Serpro verification',
  [ApiModel.VerificationDigitalIdSingpass]: 'Digital ID Singpass verification',
  [ApiModel.VerificationDigitalIdUkSharecode]: 'Digital ID UK Sharecode verification',
  [ApiModel.VerificationDocument]: 'Document verification',
  [ApiModel.VerificationEmailAddress]: 'Email Address verification',
  [ApiModel.VerificationGovernmentId]: 'Government ID verification',
  [ApiModel.VerificationGovernmentIdNfc]: 'Government ID NFC verification',
  [ApiModel.VerificationMobileDriversLicense]: 'Mobile Driver’s License verification',
  [ApiModel.VerificationPhoneNumber]: 'Phone Number verification',
  [ApiModel.VerificationSelfie]: 'Selfie verification',
};

export const VERIFICATION_TYPE_NAMES_SHORT: Record<Verification['type'], string> =
  Object.fromEntries(
    Object.entries(VERIFICATION_TYPE_NAMES).map(([key, value]) => [
      key,
      value
        .replace(/Digital ID\s+/g, '') // Remove "Digital ID"
        .replace(/\s+verification$/, ''), // Remove "verification" at the end
    ]),
  ) as Record<Verification['type'], string>;

export const VERIFICATION_TEMPLATE_TYPE_NAMES_SHORT: Record<VerificationTemplate['type'], string> =
  Object.fromEntries(
    Object.entries(VERIFICATION_TYPE_NAMES_SHORT).map(([key, value]) => [
      key.replace('verification', 'verification-template'),
      value,
    ]),
  ) as Record<VerificationTemplate['type'], string>;

export const VERIFICATION_TEMPLATE_VERSION_TYPE_NAMES_SHORT: Record<
  VerificationTemplateVersion['type'],
  string
> = Object.fromEntries(
  Object.entries(VERIFICATION_TYPE_NAMES_SHORT).map(([key, value]) => [
    key.replace('verification', 'verification-template-version'),
    value,
  ]),
) as Record<VerificationTemplateVersion['type'], string>;

const mapVerificationToCheckType = (verificationType: string): CheckType => {
  const manualMappings: Record<string, CheckType> = {
    // To break cyclical import btwn verificationCheck.ts and this file, can't do CheckType.ID
    [ApiModel.VerificationGovernmentId]: 'id' as CheckType,
    [ApiModel.VerificationGovernmentIdNfc]: 'id_nfc' as CheckType,
  };
  if (manualMappings[verificationType]) {
    return manualMappings[verificationType];
  }
  return snakeCase(verificationType.split('/')[1]) as CheckType;
};

export const VERIFICATION_TYPE_CHECK_TYPE: Record<Verification['type'], CheckType> =
  Object.fromEntries(
    Object.entries(VERIFICATION_TYPE_NAMES_SHORT).map(([verificationType, _]) => [
      verificationType,
      mapVerificationToCheckType(verificationType),
    ]),
  ) as Record<Verification['type'], CheckType>;

export const VERIFICATION_DASHBOARD_VISIBILITY_MAP: Record<Verification['type'], boolean> = {
  [ApiModel.VerificationAamva]: true,
  [ApiModel.VerificationDatabaseBusinessFootprint]: true,
  [ApiModel.VerificationDatabaseBusiness]: true,
  [ApiModel.VerificationBusinessWebsite]: true,
  // TODO: (amok) Add CertificateKorea checks once we're ready to expose the checks publicly
  [ApiModel.VerificationCertificateKorea]: false,
  [ApiModel.VerificationDatabase]: true,
  [ApiModel.VerificationDatabaseAadhaar]: true,
  [ApiModel.VerificationDatabaseStandard]: true,
  [ApiModel.VerificationDatabaseEcbsv]: true,
  [ApiModel.VerificationDatabasePhoneCarrier]: true,
  [ApiModel.VerificationDatabaseSerpro]: true,
  [ApiModel.VerificationDatabaseTin]: true,
  [ApiModel.VerificationDatabaseVatNumber]: true,
  [ApiModel.VerificationDigitalIdFinnishTrustNetwork]: true,
  [ApiModel.VerificationDigitalIdOneId]: true,
  [ApiModel.VerificationDigitalIdSerpro]: true,
  [ApiModel.VerificationDigitalIdSingpass]: false,
  [ApiModel.VerificationDigitalIdUkSharecode]: true,
  [ApiModel.VerificationDocument]: true,
  [ApiModel.VerificationEmailAddress]: true,
  [ApiModel.VerificationGovernmentId]: true,
  [ApiModel.VerificationGovernmentIdNfc]: true,
  [ApiModel.VerificationMobileDriversLicense]: false,
  [ApiModel.VerificationPhoneNumber]: true,
  [ApiModel.VerificationSelfie]: true,
};
